import { action, makeObservable, observable, runInAction } from 'mobx'

import Delivery from '~/client/src/shared/models/Delivery'
import Message from '~/client/src/shared/models/Message'
import Photo from '~/client/src/shared/models/Photo'
import DeliveriesStore from '~/client/src/shared/stores/domain/Deliveries.store'
import DeliveryStatusChangesStore from '~/client/src/shared/stores/domain/DeliveryStatusChanges.store'
import MessagesStore from '~/client/src/shared/stores/domain/MessagesStore/Messages.store'
import PhotosStore from '~/client/src/shared/stores/domain/Photos.store'
import ThreadsStore from '~/client/src/shared/stores/domain/ThreadsStore/Threads.store'
import BaseEventStore from '~/client/src/shared/stores/EventStore/BaseEvents.store'
import { REPORT_ERROR } from '~/client/src/shared/stores/EventStore/eventConstants'
import BaseActionBarStore from '~/client/src/shared/stores/ui/BaseActionBar.store'
import Guard from '~/client/src/shared/utils/Guard'

import MessageDto from '../../types/MessageDto'
import BaseFollowingsStore from '../BaseFollowings.store'
import { FileUploadingStore } from '../domain/FileUploading.store'
import TagsStore from '../domain/Tags.store'
import UserProjectsStore from '../domain/UserProjects.store'

export default class SlackBarStore extends BaseActionBarStore {
  @observable public isSending: boolean = false

  public constructor(
    eventsStore: BaseEventStore,
    threadsStore: ThreadsStore,
    messagesStore: MessagesStore,
    photosStore: PhotosStore,
    private deliveriesStore: DeliveriesStore,
    private deliveryStatusChangesStore: DeliveryStatusChangesStore,
    public fileUploadingStore: FileUploadingStore,
    userProjectsStore: UserProjectsStore,
    tagsStore: TagsStore,
    baseFollowingStore: BaseFollowingsStore,
  ) {
    super(
      eventsStore,
      threadsStore,
      messagesStore,
      photosStore,
      fileUploadingStore,
      userProjectsStore,
      tagsStore,
      baseFollowingStore,
    )

    makeObservable(this)

    Guard.requireAll({
      deliveriesStore,
      deliveryStatusChangesStore,
      tagsStore,
    })
  }

  public get entityId(): string {
    return this._entityId
  }

  @action.bound
  public async send() {
    this.isSending = true
    if (this.decodedDraftMessage) {
      this.draftMessage = this.decodedDraftMessage
    }
    this.subscribeMentionedUsers()

    if (this.fileImage) {
      await this.postImage()
    } else {
      await this.sendMessage()
    }

    runInAction(() => {
      this.fileImage = null
      this.isSending = false
    })
  }

  public get isEmptyMessage(): boolean {
    return !this.draftMessage && !this.fileImage
  }

  public async sendMessage(): Promise<MessageDto> {
    try {
      const messageText = this.draftMessage.trim()
      if (!messageText.length) {
        return Promise.resolve(null)
      }
      const thread = await this.threadsStore.create()
      const message = await this.messagesStore.post(messageText, thread)
      this.reset()
      return message
    } catch (e) {
      this.handleError(e)
    }
  }

  public async replyToThread(threadEntity: Message | Delivery) {
    await super.replyToThread(threadEntity)
  }

  public async replyOnThread(threadId: string) {
    const text = this.draftMessage.trim()
    if (!this.fileImage && !text.length) {
      return
    }
    this.reset()
    await this.postMessageWithAttachments(
      text,
      threadId,
      this.getFileCopy(this.fileImage),
    )
    this.fileImage = null
  }

  public replyToPhoto(photo: Photo) {
    const photoMessage = this.messagesStore.list.find(
      message => message.photoId === photo.id,
    )

    return this.replyToThread(photoMessage)
  }

  public closeImagePreview() {
    runInAction(() => {
      setTimeout(() => {
        runInAction(() => {
          this.shouldShowPreview = false
        })
      }, 500)
    })
  }

  public reset(): void {
    super.reset()
  }

  protected updateThreadEntity(threadEntity: Message) {
    const msg = threadEntity as Message
    const threadImg = this.photosStore.byId.get(msg.photoId)
    if (threadImg) {
      this.photosStore.save(threadImg)
    }
  }

  private async postImage() {
    const caption = this.draftMessage
    this.reset()
    try {
      const thread = await this.threadsStore.create()
      return this.postMessageWithAttachments(
        caption,
        thread,
        this.getFileCopy(this.fileImage),
      )
    } catch (e) {
      this.handleError(e)
    } finally {
      this.closeImagePreview()
    }
  }

  private handleError(error: Error): any {
    console.error(error)
    this.eventsStore.dispatch(REPORT_ERROR, 'SlackBarStore', error)
  }
}
